import { permissions } from "@/app/shared/roles-permissions/permissions";
import { AuthContext } from "@/auth/authContext";
import StatusBadge from "@/components/get-status-badge";
import routes from "@/routers/routes";
import cn from "@/utils/class-names";
import { Fragment, useContext, useEffect } from "react";
import {
    PiCaretDownBold,
    PiCirclesThreePlus,
    PiCurrencyCircleDollarDuotone,
    PiCurrencyDollarDuotone,
    PiFolderLockDuotone,
    PiGear,
    PiGitPullRequest,
    PiMetronomeDuotone,
    PiPackageDuotone,
    PiShippingContainer,
} from "react-icons/pi";
import { Link, useLocation } from "react-router-dom";
import { Collapse, Title } from "rizzui";

const APP_NAME = import.meta.env.VITE_APP_NAME || "Afro Cargo";

const routeSegmentTitles: Record<string, string> = {
    orders: "Orders",
    shipments: "Shipments",
    tracking: "Tracking",
    billing: "Billing",
    payments: "Payments",
    clients: "Clients",
    categories: "Categories",
    services: "Services",
    notifications: "Notifications",
    "roles-permissions": "User Management",
    "system-settings": "System Settings",
    partiners: "Partners",
};

function toTitleCase(value: string) {
    return value
        .split(" ")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

function isLikelyId(segment: string) {
    return (
        /^\d+$/.test(segment) ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
            segment,
        ) ||
        /^[a-zA-Z0-9_-]{20,}$/.test(segment)
    );
}

function getTabTitle(pathname: string) {
    const segments = pathname.split("/").filter(Boolean);

    if (!segments.length) {
        return "Dashboard";
    }

    const hasDynamicId = segments.some(isLikelyId);

    const namedSegments = segments
        .filter((segment) => !isLikelyId(segment))
        .map((segment) => decodeURIComponent(segment).toLowerCase())
        .map((segment) => {
            if (routeSegmentTitles[segment]) {
                return routeSegmentTitles[segment];
            }

            return toTitleCase(segment.replace(/[-_]/g, " "));
        });

    if (!namedSegments.length) {
        return "Dashboard";
    }

    if (hasDynamicId) {
        return `${namedSegments[namedSegments.length - 1]} Details`;
    }

    return namedSegments[namedSegments.length - 1];
}

export function SidebarMenu() {
    const location = useLocation();
    const { role } = useContext(AuthContext); // Move useContext outside of menuItems function

    const hasPermission = (permissions: string[]) => {
        return permissions.some((permission: string) =>
            role?.includes(permission),
        );
    };

    const menuItems = [
        // label start
        {
            name: "Overview",
        },
        {
            name: "Dashboard",
            href: routes.orders.dashboard,
            icon: <PiPackageDuotone />,
            auth: hasPermission([
                permissions.orders.view,
                permissions.system.admin,
                permissions.system.customer,
                permissions.system.user,
            ]),
        },
        // label start
        {
            name: "Cargo Services",
        },
        // label end
        {
            name: "Orders",
            href: routes.orders.list,
            icon: <PiGitPullRequest />,
            auth: hasPermission([
                permissions.orders.view,
                permissions.system.admin,
                permissions.system.customer,
                permissions.system.user,
            ]),
        },
        {
            name: "Request Order",
            href: routes.orders.createOrder,
            icon: <PiGitPullRequest />,
            auth: hasPermission([
                permissions.orders.create,
                permissions.system.admin,
                permissions.system.customer,
                permissions.system.user,
            ]),
        },
        {
            name: "Request Shipment",
            href: routes.orders.createAgentOrder,
            icon: <PiShippingContainer />,
            auth: hasPermission([
                permissions.orders.create,
                permissions.system.admin,
                permissions.system.customer,
                permissions.system.user,
            ]),
        },
        {
            name: "Payments",
            href: routes.payments.list,
            icon: <PiCurrencyDollarDuotone />,
            auth: hasPermission([
                permissions.transactions.getTransactionByOrder,
                permissions.system.admin,
                permissions.system.customer,
                permissions.system.user,
            ]),
        },
        {
            name: "Clients",
            href: "#",
            icon: <PiMetronomeDuotone />,
            auth: hasPermission([
                permissions.products.view,
                permissions.system.admin,
                permissions.system.user,
            ]),
            dropdownItems: [
                {
                    name: "List",
                    href: routes.clients.list,
                    auth: hasPermission([
                        permissions.products.view,
                        permissions.system.admin,
                        permissions.system.user,
                    ]),
                },
                {
                    name: "Create",
                    href: routes.clients.create,
                    auth: hasPermission([
                        permissions.products.create,
                        permissions.system.admin,
                        permissions.system.user,
                    ]),
                },
            ],
        },
        {
            name: "Category",
            href: routes.categories.list,
            icon: <PiCirclesThreePlus />,
            auth: hasPermission([
                permissions.categories.view,
                permissions.system.admin,
                permissions.system.user,
            ]),
        },
        {
            name: "Reports",
            href: "#",
            icon: <PiMetronomeDuotone />,
            auth: hasPermission([
                permissions.reports.view,
                permissions.system.admin,
                permissions.system.customer,
                permissions.system.user,
            ]),
            dropdownItems: [
                {
                    name: "Order Report",
                    href: routes.orders.report,
                    auth: hasPermission([
                        permissions.reports.view,
                        permissions.system.admin,
                        permissions.system.customer,
                        permissions.system.user,
                    ]),
                },
            ],
        },
        {
            name: "System",
            auth: hasPermission([
                permissions.reports.view,
                permissions.system.admin,
                permissions.system.user,
            ]),
        },
        {
            name: "System Settings",
            href: "#",
            icon: <PiGear />,
            auth: hasPermission([
                permissions.reports.view,
                permissions.system.admin,
            ]),
            dropdownItems: [
                {
                    name: "General",
                    href: routes.settings.general.create,
                    auth: hasPermission([permissions.system.admin]),
                },
                {
                    name: "Services",
                    href: routes.settings.services.list,
                    auth: hasPermission([permissions.system.admin]),
                },
                {
                    name: "Partners",
                    href: routes.settings.partners.list,
                    auth: hasPermission([permissions.system.admin]),
                },
                {
                    name: "Notifications",
                    href: routes.settings.notifications,
                    auth: hasPermission([permissions.system.admin]),
                },
            ],
        },
        {
            name: "User Management",
            href: "#",
            icon: <PiFolderLockDuotone />,
            auth: hasPermission([
                permissions.users.view,
                permissions.system.admin,
                permissions.system.user,
            ]),
            dropdownItems: [
                {
                    name: "Users",
                    href: routes.userManagement.users,
                    auth: hasPermission([permissions.system.admin]),
                },
                {
                    name: "Roles",
                    href: routes.userManagement.roles,
                    auth: hasPermission([permissions.system.admin]),
                },
            ],
        },
    ];

    useEffect(() => {
        document.title = `${APP_NAME} | ${getTabTitle(location.pathname)}`;
    }, [location.pathname]);

    return (
        <div className="mt-4 pb-3 3xl:mt-6">
            {menuItems.map((item, index) => {
                if (item.auth === false) return null; // Hide items without permission
                const isActive = location.pathname === (item?.href as string);
                const pathnameExistInDropdowns: any =
                    item?.dropdownItems?.filter(
                        (dropdownItem) =>
                            dropdownItem.href === location.pathname,
                    );
                const isDropdownOpen = Boolean(
                    pathnameExistInDropdowns?.length,
                );

                return (
                    <Fragment key={item.name + "-" + index}>
                        {item?.href ? (
                            <>
                                {item?.dropdownItems ? (
                                    <Collapse
                                        defaultOpen={isDropdownOpen}
                                        header={({ open, toggle }) => (
                                            <div
                                                onClick={toggle}
                                                className={cn(
                                                    "group relative mx-3 flex cursor-pointer items-center justify-between rounded-md px-3 py-2 font-medium lg:my-1 2xl:mx-5 2xl:my-2",
                                                    isDropdownOpen
                                                        ? "before:top-2/5 text-primary before:absolute before:-start-3 before:block before:h-4/5 before:w-1 before:rounded-ee-md before:rounded-se-md before:bg-primary 2xl:before:-start-5"
                                                        : "text-gray-700 transition-colors duration-200 hover:bg-gray-100 dark:text-gray-700/90 dark:hover:text-gray-700",
                                                )}
                                            >
                                                <span className="flex items-center">
                                                    {item?.icon && (
                                                        <span
                                                            className={cn(
                                                                "me-2 inline-flex h-5 w-5 items-center justify-center rounded-md [&>svg]:h-[20px] [&>svg]:w-[20px]",
                                                                isDropdownOpen
                                                                    ? "text-primary"
                                                                    : "text-gray-800 dark:text-gray-500 dark:group-hover:text-gray-700",
                                                            )}
                                                        >
                                                            {item?.icon}
                                                        </span>
                                                    )}
                                                    {item.name}
                                                </span>

                                                <PiCaretDownBold
                                                    strokeWidth={3}
                                                    className={cn(
                                                        "h-3.5 w-3.5 -rotate-90 text-gray-500 transition-transform duration-200 rtl:rotate-90",
                                                        open &&
                                                            "rotate-0 rtl:rotate-0",
                                                    )}
                                                />
                                            </div>
                                        )}
                                    >
                                        {item?.dropdownItems?.map(
                                            (dropdownItem, index) => {
                                                if (dropdownItem.auth === false)
                                                    return null; // Hide items without permission
                                                const isChildActive =
                                                    location.pathname ===
                                                    (dropdownItem?.href as string);

                                                return (
                                                    <Link
                                                        to={dropdownItem?.href}
                                                        key={
                                                            dropdownItem?.name +
                                                            index
                                                        }
                                                        className={cn(
                                                            "mx-3.5 mb-0.5 flex items-center justify-between rounded-md px-3.5 py-2 font-medium capitalize last-of-type:mb-1 lg:last-of-type:mb-2 2xl:mx-5",
                                                            isChildActive
                                                                ? "text-primary"
                                                                : "text-gray-500 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-900",
                                                        )}
                                                    >
                                                        <div className="flex items-center truncate">
                                                            <span
                                                                className={cn(
                                                                    "me-[18px] ms-1 inline-flex h-1 w-1 rounded-full bg-current transition-all duration-200",
                                                                    isChildActive
                                                                        ? "bg-primary ring-[1px] ring-primary"
                                                                        : "opacity-40",
                                                                )}
                                                            />{" "}
                                                            <span className="truncate">
                                                                {
                                                                    dropdownItem?.name
                                                                }
                                                            </span>
                                                        </div>
                                                        {dropdownItem?.badge
                                                            ?.length ? (
                                                            <StatusBadge
                                                                status={
                                                                    dropdownItem?.badge
                                                                }
                                                            />
                                                        ) : null}
                                                    </Link>
                                                );
                                            },
                                        )}
                                    </Collapse>
                                ) : (
                                    <Link
                                        to={item?.href}
                                        className={cn(
                                            "group relative mx-3 my-0.5 flex items-center justify-between rounded-md px-3 py-2 font-medium capitalize lg:my-1 2xl:mx-5 2xl:my-2",
                                            isActive
                                                ? "before:top-2/5 text-primary before:absolute before:-start-3 before:block before:h-4/5 before:w-1 before:rounded-ee-md before:rounded-se-md before:bg-primary 2xl:before:-start-5"
                                                : "text-gray-700 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-700/90",
                                        )}
                                    >
                                        <div className="flex items-center truncate">
                                            {item?.icon && (
                                                <span
                                                    className={cn(
                                                        "me-2 inline-flex size-5 items-center justify-center rounded-md [&>svg]:size-5",
                                                        isActive
                                                            ? "text-primary"
                                                            : "text-gray-800 dark:text-gray-500 dark:group-hover:text-gray-700",
                                                    )}
                                                >
                                                    {item?.icon}
                                                </span>
                                            )}
                                            <span className="truncate">
                                                {item.name}
                                            </span>
                                        </div>
                                        {item?.badge?.length ? (
                                            <StatusBadge status={item?.badge} />
                                        ) : null}
                                    </Link>
                                )}
                            </>
                        ) : (
                            <Title
                                as="h6"
                                className={cn(
                                    "mb-2 truncate px-6 text-xs font-normal uppercase tracking-widest text-gray-500 2xl:px-8",
                                    index !== 0 && "mt-6 3xl:mt-7",
                                )}
                            >
                                {item.name}
                            </Title>
                        )}
                    </Fragment>
                );
            })}
        </div>
    );
}
